import { useEffect, useState } from 'react';
import ItemTag from './item/ItemTag';
import { TagCache } from '../../lib/cache/TagCache';
import { EventSections } from '../../lib/events/EventLogger';
import { PanelButtons } from './panels/PanelButtons';
import { P2GBox, P2GGrid, P2GTextField, SvgAdd, P2GChip, P2GIcon, P2GText } from '@parcel2go/core-ui';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { sanitiseKey } from '~/lib/utils/translation';
import { onlyUnique } from '~/lib/utils/array';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import CdnIconProxy from '~/apps/account-linking/components/CdnIconProxy';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';

const restrictedTags = ['ReturnCollection'];

interface props {
  selected: string[];
  save: (tags: string[]) => void;
  cancel?: () => void;
  section: string;
}

export const AddTag: React.FC<props> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const [changes, setChanges] = useState(false);
  const [tags, setTags] = useState([...props.selected]);
  const [tag, setTag] = useState<string>('');
  const possibleDefaults = TagCache.filter((x) => !tags.includes(x));
  const { track } = useSmartSendAnalytics();

  useEffect(() => {
    track({
      type: 'tags_loaded',
      payload: {
        ...GenerateBaseAnalyticsPayload([props.section, EventSections.Tags])
      }
    });
  }, []);

  const addTag = (t: string) => {
    if (!restrictedTags.includes(t)) {
      setTags([...tags, t]);
    }
    setTag('');
    setChanges(true);
  };

  const remove = (t: string) => {
    setTags([...tags].filter((x) => x !== t));
    setChanges(true);
  };

  const save = () => {
    props.save(tags.filter(onlyUnique));
  };

  return (
    <P2GBox display="flex" flexDirection="column" gap={2} pb={1} pt={1}>
      <P2GTextField
        fullWidth
        id="MyNewTag"
        label={t('TAG_NAME_INPUT_LABEL', { defaultValue: 'Tag Name...' })}
        onChange={(v) => {
          setTag(v.currentTarget.value);
        }}
        value={tag}
        onKeyPress={(e) => {
          if (e.key === 'Enter') {
            addTag(tag);
          }
        }}
        endAdornment={
          <CdnIconProxy onClick={() => addTag(tag)} palette="primary" src="/theme/icons/Add.svg">
            <SvgAdd />
          </CdnIconProxy>
        }
      />

      {tags.length > 0 && (
        <>
          <P2GText variant="caption">Tags</P2GText>
          <P2GGrid container spacing={1}>
            {tags.map((t) => (
              <P2GGrid item key={t}>
                <ItemTag color={props.selected.includes(t) ? 'info' : 'primary'} tag={t} onRemove={() => remove(t)} />
              </P2GGrid>
            ))}
          </P2GGrid>
        </>
      )}

      {possibleDefaults.length > 0 && (
        <>
          <P2GText variant="caption">Suggested Tags</P2GText>
          <P2GGrid container spacing={1}>
            {possibleDefaults.map((tag) => (
              <P2GGrid item key={tag}>
                <P2GChip
                  color="info"
                  size="small"
                  label={t(sanitiseKey(`TAG_SUGGESTION_${tag}`), { defaultValue: tag })}
                  clickable
                  onClick={() => addTag(tag)}
                  icon={
                    <P2GIcon>
                      <SvgAdd />
                    </P2GIcon>
                  }
                />
              </P2GGrid>
            ))}
          </P2GGrid>
        </>
      )}
      <PanelButtons
        saveDisabled={!changes}
        onSave={save}
        onCancel={props.cancel}
        cancelText={t('CANCEL_BUTTON', { defaultValue: 'Cancel' })}
      />
    </P2GBox>
  );
};
